"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Request first, stock on Approve",
    rule: "Add Transfer Request / Transfer saves a Pending request. Source qty is not deducted until Approve. Reject leaves stock where it was. There is no In Transit, Receive, or Cancel on this screen",
  },
  {
    topic: "Statuses",
    rule: "Pending, Approved, Rejected. Approve and Reject need Edit and only work on Pending. Cancelled exists in the database but is hidden from the list",
  },
  {
    topic: "Transfer#",
    rule: "Auto TRF1, TRF2, … from the last request. There is no number-preferences gear on this modal",
  },
  {
    topic: "Tracked inventory items only",
    rule: "The Item picker is Track inventory goods. Service items and Track inventory off never appear. Approve also refuses them. One item per manual request. Quantity must be > 0 and cannot exceed Available Quantity at the from warehouse",
  },
  {
    topic: "Same warehouse blocked",
    rule: "From and To cannot be the same warehouse. The To Warehouse picker already hides the from warehouse",
  },
  {
    topic: "Approve moves FIFO batches",
    rule: "Deducts from the source warehouse (FIFO remaining qty) and adds matching batches at the destination, including purchase price and vendor batch fields. If the item has no row at the destination yet, one is created",
  },
  {
    topic: "Production Order Transfers",
    rule: "Created from a production order, not from Add Transfer Request. Approve moves each raw material's required qty and sets the production order to Inventory Issued. Reject sets Inventory Rejected. Manpower (optional) is offered only on production Approve / Reject",
  },
  {
    topic: "No E-Way Bill, no edit, no delete, no Duplicate, no list Export",
    rule: "Detail has Overview and Stock Movement after it has posted. No GST, no attachments, no primary-bank gate",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating a manual transfer request" },
      { heading: "2. Manual vs Production Order tabs" },
      { heading: "3. The list, search, and filters" },
      { heading: "4. Approving a request" },
      { heading: "5. Rejecting a request" },
      { heading: "6. Detail and Stock Movement" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function InventoryTransferPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Inventory Transfer
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone moving stock from one warehouse to another — after
            someone Approves
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            An <B>Inventory Transfer</B> is a request. Prefix <B>TRF</B>.{" "}
            <B>Transfer</B> on the modal creates <B>Pending</B>. Stock leaves
            the source and arrives at the destination only when someone with
            Edit clicks <B>Approve Transfer</B>. There is no in-transit bucket
            and no Receive step.
          </p>
          <Callout type="note">
            Only items with <B>Track inventory</B> on can be transferred.{" "}
            <B>Service</B> items and goods with Track inventory off do not
            appear on the Item picker and cannot be approved.
          </Callout>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating a manual transfer request
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            List <B>Add Transfer Request</B> (create). Modal title{" "}
            <B>Transfer Inventory</B>. Buttons <B>Cancel</B> and <B>Transfer</B>
            .
          </p>
          <Definitions
            items={[
              {
                term: "Item",
                desc: (
                  <>
                    Required. Tracked inventory goods only — Service and Track
                    inventory off are not in this list. Picking an item loads
                    which divisions / branches / warehouses actually hold it.
                  </>
                ),
              },
              {
                term: "From Division / Branch / Warehouse",
                desc: (
                  <>
                    Required. Only locations where this item has stock.
                    Available Quantity fills from that warehouse and is
                    read-only.
                  </>
                ),
              },
              {
                term: "Transfer Date",
                desc: <>Required. Defaults to today.</>,
              },
              {
                term: "To Division / Branch / Warehouse",
                desc: (
                  <>
                    Required. Any warehouse on the To Branch except the from
                    warehouse.
                  </>
                ),
              },
              {
                term: "Transfer Quantity",
                desc: (
                  <>
                    Required, greater than 0, not more than Available Quantity.
                    Enabled after From Warehouse.
                  </>
                ),
              },
              {
                term: "Transfer Notes",
                desc: <>Optional.</>,
              },
            ]}
          />
          <Callout type="note">
            Only <B>Track inventory</B> items show on Item. <B>Service</B> or
            Track inventory off items do not. This modal is one item and a
            manual request — it does not create a Production Order Transfer.
          </Callout>
          <Screenshot caption="Screenshot: Transfer Inventory modal with Item, From Warehouse, Available Quantity, To Warehouse, Transfer Quantity, and Transfer." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Manual vs Production Order tabs
          </h3>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Tab
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    What the row is
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#F5F5F5]">
                  <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[200px]">
                    Manual Transfers
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                    One item, one quantity. Columns: Transfer#, Item Name,
                    Transfer Date, From / To Warehouse, Quantity, Status
                  </td>
                </tr>
                <tr>
                  <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top">
                    Production Order Transfers
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                    Raised from a production order. Columns: Transfer#,
                    Production Order, Transfer Date, From / To Warehouse, Raw
                    Materials (View), Status. Approve issues every raw-material
                    line
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <Screenshot caption="Screenshot: Inventory Transfer with Manual Transfers and Production Order Transfers tabs." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. The list, search, and filters
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Title Inventory Transfer with a count. Search (Transfer#, item name,
            warehouse name, status, reference). Column picker. Filter drawer:
            Status, Division, Branch. Action: View Details, Approve Request,
            Reject Request. Row click → detail. No list export.
          </p>
          <Screenshot caption="Screenshot: Inventory Transfer list with Add Transfer Request, status filter, and Approve / Reject on a Pending row." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Approving a request
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            List or detail, Pending only, Edit permission. Modal{" "}
            <B>Approve Transfer</B>. Shows Transfer ID, item (or Production
            Order), date, warehouses, quantity, status. <B>Approve Transfer</B>{" "}
            moves stock immediately.
          </p>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Production Approve also offers Department and Manpower (optional).
            On success the production order becomes Inventory Issued.
          </p>
          <Callout type="warning">
            If source stock is no longer enough, Approve fails (raw materials
            are checked strictly on production). Nothing is half-moved.
          </Callout>
          <Screenshot caption="Screenshot: Approve Transfer modal with transfer details and Approve Transfer." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Rejecting a request
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Pending only. Status becomes Rejected. Stock never moved, so nothing
            is reversed. A production order is set to Inventory Rejected.
            Manpower can be stored on that reject as well.
          </p>

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Detail and Stock Movement
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Title Inventory Transfer Request Details. Overview: Transfer ID,
            Production Order when present, date, status, item, quantity, from/to
            warehouse, notes, raw materials table when production. Sidebar
            Approve / Reject while Pending.
          </p>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Tab <B>Stock Movement</B> shows the batches this transfer added or
            used after Approve. Check View Inventory on both warehouses to see
            available qty change.
          </p>
          <Screenshot caption="Screenshot: Inventory Transfer Request Details with Overview and Stock Movement." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/inventory/view-inventory"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              View Inventory <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Source qty drops and destination qty rises after Approve
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/warehouse-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Warehouse Master <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              From and To warehouses on the request
            </p>
          </Link>
          <Link
            href="/documentation/books/production/add-production"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Add Production <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where a Production Order Transfer is raised
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
